import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ContactType = 'whatsapp' | 'call' | 'email' | 'in_person';
export type Outcome = 'no_answer' | 'promised' | 'refused' | 'negotiating' | 'partial_payment' | 'paid' | 'renegotiated' | 'wrong_number' | 'callback_requested';
export type ContactReason = 'collection' | 'follow_up' | 'promise_check' | 'renegotiation' | 'confirmation';

export interface CollectionHistoryEntry {
  id: string;
  payment_id: string;
  contact_id: string;
  contact_date: string;
  contact_type: ContactType;
  notes: string | null;
  promised_date: string | null;
  promised_amount: number | null;
  outcome: Outcome;
  reason: ContactReason;
  callback_date: string | null;
  next_action: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CreateCollectionHistoryInput {
  payment_id: string;
  contact_id: string;
  contact_type: ContactType;
  outcome: Outcome;
  reason: ContactReason;
  notes?: string;
  promised_date?: string;
  promised_amount?: number;
  callback_date?: string;
  next_action?: string;
}

const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  whatsapp: 'WhatsApp',
  call: 'Ligação',
  email: 'Email',
  in_person: 'Presencial',
};

const OUTCOME_LABELS: Record<Outcome, string> = {
  no_answer: 'Não atendeu',
  promised: 'Prometeu pagar',
  refused: 'Recusou',
  negotiating: 'Negociando',
  partial_payment: 'Pagamento parcial',
  paid: 'Pagou',
  renegotiated: 'Contrato renegociado',
  wrong_number: 'Número errado',
  callback_requested: 'Pediu retorno',
};

const REASON_LABELS: Record<ContactReason, string> = {
  collection: 'Cobrança de parcela',
  follow_up: 'Retorno de contato',
  promise_check: 'Verificar promessa',
  renegotiation: 'Renegociação',
  confirmation: 'Confirmar pagamento',
};

export const getContactTypeLabel = (type: ContactType) => CONTACT_TYPE_LABELS[type];
export const getOutcomeLabel = (outcome: Outcome) => OUTCOME_LABELS[outcome];
export const getReasonLabel = (reason: ContactReason) => REASON_LABELS[reason];

// Fetch collection history for a specific payment
export function usePaymentCollectionHistory(paymentId: string) {
  return useQuery({
    queryKey: ['collection-history', 'payment', paymentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_history')
        .select('*')
        .eq('payment_id', paymentId)
        .order('contact_date', { ascending: false });

      if (error) throw error;
      return data as CollectionHistoryEntry[];
    },
    enabled: !!paymentId,
  });
}

// Fetch collection history for a specific contact
export function useContactCollectionHistory(contactId: string) {
  return useQuery({
    queryKey: ['collection-history', 'contact', contactId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_history')
        .select('*')
        .eq('contact_id', contactId)
        .order('contact_date', { ascending: false });

      if (error) throw error;
      return data as CollectionHistoryEntry[];
    },
    enabled: !!contactId,
  });
}

// Fetch last collection contact for multiple payments (for table display)
export function useLastCollectionContacts(paymentIds: string[]) {
  return useQuery({
    queryKey: ['collection-history', 'last-contacts', paymentIds],
    queryFn: async () => {
      if (!paymentIds.length) return {};

      // Get the most recent entry for each payment
      const { data, error } = await supabase
        .from('collection_history')
        .select('*')
        .in('payment_id', paymentIds)
        .order('contact_date', { ascending: false });

      if (error) throw error;

      // Group by payment_id and get the most recent one
      const lastContactByPayment: Record<string, CollectionHistoryEntry> = {};
      for (const entry of data as CollectionHistoryEntry[]) {
        if (!lastContactByPayment[entry.payment_id]) {
          lastContactByPayment[entry.payment_id] = entry;
        }
      }

      return lastContactByPayment;
    },
    enabled: paymentIds.length > 0,
  });
}

// Create a new collection history entry
export function useCreateCollectionHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCollectionHistoryInput) => {
      const { data: user } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('collection_history')
        .insert({
          ...input,
          created_by: user.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Contato registrado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['collection-history'] });
      queryClient.invalidateQueries({ queryKey: ['promises'] });
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['receivables-ops-meta'] });
      queryClient.invalidateQueries({ queryKey: ['collection-dashboard'] });
    },
    onError: (error) => {
      console.error('Error creating collection history:', error);
      toast.error('Erro ao registrar contato');
    },
  });
}
